import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Container, Spinner, Alert } from "react-bootstrap";
import { userActions } from "../actions/user.actions";
import { useDispatch } from "react-redux";
import Utils from "../utils";

function AzureAdCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [error, setError] = useState("");

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const searchParams = new URLSearchParams(location.search);
        const code = searchParams.get("code");
        const state = searchParams.get("state");
        const errorParam = searchParams.get("error");
        const errorDescription = searchParams.get("error_description");

        if (errorParam) {
          setError(`Authentication failed: ${errorDescription || errorParam}`);
          setTimeout(() => navigate("/login"), 3000);
          return;
        }

        if (!code || !state) {
          setError("Invalid callback - missing authorization code or state");
          setTimeout(() => navigate("/login"), 3000);
          return;
        }

        // Use state (nonce) as a lookup key into sessionStorage — trusted data never comes from URL
        const oauthStateStr = sessionStorage.getItem(`oauthState_${state}`);
        sessionStorage.removeItem(`oauthState_${state}`);
        if (!oauthStateStr) {
          setError("Invalid authentication request. Please try signing in again.");
          setTimeout(() => navigate("/login"), 3000);
          return;
        }
        const { email, keepMeSignedIn, redirectUrl } = JSON.parse(oauthStateStr);

        // Get Azure AD config from localStorage
        const azureAdConfigStr = localStorage.getItem("azureAdConfig");

        if (!azureAdConfigStr) {
          setError("Azure AD configuration not found. Please try signing in again.");
          setTimeout(() => navigate("/login"), 3000);
          return;
        }

        const azureAdConfig = JSON.parse(azureAdConfigStr);

        // Exchange authorization code for access token with Microsoft
        const tokenEndpoint = `https://login.microsoftonline.com/${azureAdConfig.tenantId}/oauth2/v2.0/token`;

        const tokenRequestBody = new URLSearchParams({
          client_id: azureAdConfig.clientId,
          scope: 'openid profile email',
          code: code,
          redirect_uri: azureAdConfig.redirectUri,
          grant_type: 'authorization_code',
          code_verifier: azureAdConfig.codeVerifier
        });

        console.info("[AzureAD] requesting token", {
          tokenEndpoint,
          tenantId: azureAdConfig.tenantId,
          clientId: azureAdConfig.clientId,
          redirectUri: azureAdConfig.redirectUri,
          timestamp: new Date().toISOString(),
        });

        let tokenResponse;
        try {
          tokenResponse = await fetch(tokenEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: tokenRequestBody
          });
        } catch (fetchErr) {
          // Request never reached Microsoft — network/CORS/proxy block, not a rejection from Entra ID
          console.error("[AzureAD] token fetch failed before receiving a response", {
            tenantId: azureAdConfig.tenantId,
            name: fetchErr?.name,
            message: fetchErr?.message,
          });
          setError(`Failed to reach Microsoft token endpoint: ${fetchErr?.message || "network error"}`);
          setTimeout(() => navigate("/login"), 3000);
          return;
        }

        if (!tokenResponse.ok) {
          const errorText = await tokenResponse.text();
          let errorDetail = tokenResponse.statusText;
          try {
            const errorJson = JSON.parse(errorText);
            errorDetail = errorJson.error_description || errorJson.error || errorDetail;
          } catch {
            // Not JSON — fall back to statusText
          }
          console.error("[AzureAD] token endpoint returned an error", {
            tenantId: azureAdConfig.tenantId,
            status: tokenResponse.status,
            statusText: tokenResponse.statusText,
            body: errorText,
          });
          setError(`Failed to exchange code for token: ${errorDetail}`);
          setTimeout(() => navigate("/login"), 3000);
          return;
        }

        const tokenData = await tokenResponse.json();
        // Use ID token instead of access token - ID token is for our app, access token is for Graph API
        const microsoftIdToken = tokenData.id_token;

        if (!microsoftIdToken) {
          console.error("[AzureAD] token response had no id_token", { tenantId: azureAdConfig.tenantId, keys: Object.keys(tokenData) });
          setError("Failed to get ID token from Microsoft");
          setTimeout(() => navigate("/login"), 3000);
          return;
        }

        console.info("[AzureAD] token exchange succeeded, sending ID token to backend", {
          tenantId: azureAdConfig.tenantId,
          timestamp: new Date().toISOString(),
        });

        // Now send the Microsoft ID token to our backend
        dispatch(
          userActions.azureAdLogin(
            microsoftIdToken,
            email,
            keepMeSignedIn,
            redirectUrl || Utils.getHomeRoute()
          )
        );

        // Clean up localStorage
        localStorage.removeItem("azureAdConfig");
      } catch (err) {
        console.error("[AzureAD] unhandled error during callback", {
          name: err?.name,
          message: err?.message,
          stack: err?.stack,
        });
        setError("An error occurred during authentication");
        setTimeout(() => navigate("/login"), 3000);
      }
    };

    handleCallback();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="centered-div">
      <Container className="login-box bg-white p-5 rounded border border-secondary mt-5 text-center">
        {!error ? (
          <>
            <Spinner animation="border" variant="primary" className="mb-3" />
            <h5>Completing Azure AD authentication...</h5>
            <p className="text-muted">Please wait while we sign you in.</p>
          </>
        ) : (
          <>
            <Alert variant="danger">{error}</Alert>
            <p className="text-muted">Redirecting to login page...</p>
          </>
        )}
      </Container>
    </div>
  );
}

export default AzureAdCallback;
